import { TariffTypeEnum } from './tariff.constants';
import {
  ITariff,
  ITariffData,
  ITariffException,
  ITariffExceptionData,
  ITariffRegion,
  ITariffRegionData
} from './tariff.types';

import { AbstractModel } from '../base';

import { Country, ICountry } from '@/models/country';
import { HSCode, IHSCode } from '../hs-code';

export class TariffRegion extends AbstractModel implements ITariffRegion {
  readonly id?: number;
  readonly name: string;
  readonly countries: (ICountry | string)[] = [];

  constructor(data: ITariffRegionData) {
    super();

    this.validate(data, ['name']);

    this.name = data.name!;

    if (data.id) {
      this.id = data.id;
    }

    if (data.countries) {
      this.countries = data.countries.map(country =>
        typeof country === 'string' ? country : new Country(country)
      );
    }
  }

  toString(): string {
    return this.name;
  }
}

export class Tariff extends AbstractModel implements ITariff {
  readonly id?: number;
  readonly tariff_type: TariffTypeEnum;
  readonly destination_country: ICountry | string;
  readonly tariff_rate: string;

  readonly origin_country: ICountry | string | null = null;
  readonly origin_region: ITariffRegion | number | null = null;
  readonly hs_code: IHSCode | string | null = null;

  readonly effective_date: string | null = null;
  readonly expiration_date: string | null = null;

  constructor(data: ITariffData) {
    super();

    const requiredFields: (keyof ITariffData)[] = [
      'tariff_type',
      'tariff_rate',
      'destination_country'
    ];

    this.validate(data, requiredFields);

    if (data.id) {
      this.id = data.id;
    }

    this.tariff_rate = data.tariff_rate!;
    this.tariff_type = data.tariff_type!;

    this.destination_country =
      typeof data.destination_country === 'string'
        ? data.destination_country
        : new Country(data.destination_country!);

    if (data.hs_code) {
      this.hs_code =
        typeof data.hs_code === 'string'
          ? data.hs_code
          : new HSCode(data.hs_code);
    }

    if (data.origin_country) {
      this.origin_country =
        typeof data.origin_country === 'string'
          ? data.origin_country
          : new Country(data.origin_country);
    }

    if (data.origin_region) {
      this.origin_region =
        typeof data.origin_region === 'number'
          ? data.origin_region
          : new TariffRegion(data.origin_region);
    }
    if (data.effective_date) {
      this.effective_date = data.effective_date;
    }
    if (data.expiration_date) {
      this.expiration_date = data.expiration_date;
    }
  }

  toString(): string {
    if (this.target)
      return `${this.target} - ${this.destination_country} (${this.tariff_rate}%)`;
    else if (this.destination_country)
      return `${this.destination_country} (${this.tariff_rate}%)`;

    return `Global - ${this.tariff_rate}%`;
  }

  get target(): string {
    let _target = '';
    if (this.tariff_type === TariffTypeEnum.Country && this.origin_country) {
      _target = this.origin_country!.toString();
    } else if (
      this.tariff_type === TariffTypeEnum.Region &&
      this.origin_region
    ) {
      _target = this.origin_region!.toString();
    } else if (this.tariff_type === TariffTypeEnum.HSCode && this.hs_code) {
      _target = this.hs_code!.toString();
    }

    return _target;
  }
}

export class TariffException extends AbstractModel implements ITariffException {
  readonly id?: number;
  readonly item: string | null = null;
  readonly hs_code: string | null = null;
  readonly origin_country: ICountry | string | null = null;
  readonly description: string = '';
  readonly destination_country: ICountry | string;
  readonly tariff_rate: string;

  constructor(data: ITariffExceptionData) {
    super();

    this.validate(data, ['destination_country', 'tariff_rate']);

    if (data.id) {
      this.id = data.id;
    }

    this.destination_country =
      typeof data.destination_country === 'string'
        ? data.destination_country
        : new Country(data.destination_country!);

    this.tariff_rate = data.tariff_rate!;

    if (data.item) {
      this.item = data.item;
    }

    if (data.hs_code) {
      this.hs_code = data.hs_code;
    }

    if (data.origin_country) {
      this.origin_country =
        typeof data.origin_country === 'string'
          ? data.origin_country
          : new Country(data.origin_country);
    }

    if (data.description) {
      this.description = data.description;
    }
  }

  get target(): string {
    let _target = '';
    if (this.item) {
      _target += this.item.toString();
    }
    if (this.hs_code) {
      if (this.item) _target += ' - ';
      _target += this.hs_code!.toString();
    }
    if (this.origin_country) {
      if (this.hs_code || this.item) _target += ' - ';
      _target += this.origin_country!.toString();
    }

    return _target;
  }

  toString(): string {
    return `${this.target} - ${this.destination_country} (${this.tariff_rate}%)`;
  }
}
